import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const MTLicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 24,
      cycleYears: 2,
      terminology: 'LU',
      highlights: [
        '24 Learning Units every 2 years',
        'AIA CES-accredited courses accepted',
        'Health, Safety & Welfare (HSW) hours strongly recommended',
        'Biennial renewal by January 1 of odd years'
      ],
      stateBoard: 'Montana Board of Architects and Landscape Architects',
      boardUrl: 'https://boards.bsd.dli.mt.gov/arc'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 30,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: [
        '30 PDH every 2 years',
        '1 PDH in engineering ethics required',
        'Self-study allowed up to 10 PDH',
        'Biennial renewal by January 1 of even years'
      ],
      stateBoard: 'Montana Board of Professional Engineers and Land Surveyors',
      boardUrl: 'https://boards.bsd.dli.mt.gov/eng'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 80,
      cycleYears: 2,
      terminology: 'CPE',
      highlights: [
        '80 CPE hours every 2 years',
        '4 hours in professional ethics per 2-year period',
        'Minimum 20 hours per calendar year',
        'Biennial renewal by December 31 of even years'
      ],
      stateBoard: 'Montana Board of Public Accountants',
      boardUrl: 'https://boards.bsd.dli.mt.gov/pac'
    },
    {
      id: 'LAW',
      name: 'Attorney',
      icon: '⚖️',
      credits: 15,
      cycleYears: 1,
      terminology: 'CLE',
      highlights: [
        '15 CLE credits per year',
        '1 hour in legal ethics or professional responsibility',
        'Up to 6 credits via online/self-study',
        'Annual compliance deadline: December 31'
      ],
      stateBoard: 'Montana State Bar',
      boardUrl: 'https://www.montanabar.org/page/CLE'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 0,
      cycleYears: 2,
      terminology: 'competency',
      highlights: [
        'No mandatory CE hour requirement for standard RN renewal',
        'Must demonstrate continued competency',
        'Biennial renewal by birth month',
        'APRNs may have additional continuing education requirements'
      ],
      stateBoard: 'Montana State Board of Nursing',
      boardUrl: 'https://boards.bsd.dli.mt.gov/nur'
    },
    {
      id: 'PHARM',
      name: 'Pharmacist',
      icon: '💊',
      credits: 30,
      cycleYears: 2,
      terminology: 'CE hours',
      highlights: [
        '30 CE hours every 2 years',
        '1 hour in Montana pharmacy law or regulations',
        'ACPE-accredited programs required',
        'Biennial renewal by October 31 of even years'
      ],
      stateBoard: 'Montana Board of Pharmacy',
      boardUrl: 'https://boards.bsd.dli.mt.gov/pha'
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Montana Professional License Renewal Requirements",
      "description": "Complete guide to Montana professional license renewal CE requirements for Architects, Engineers, CPAs, Attorneys, Nurses, and Pharmacists.",
      "url": "https://cectrack.com/mt-license-renewal",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cectrack.com" },
          { "@type": "ListItem", "position": 2, "name": "Montana License Renewal", "item": "https://cectrack.com/mt-license-renewal" }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do Montana architects need for license renewal?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Montana architects must complete 24 Learning Units (LU) every 2 years. AIA CES-accredited courses are accepted, and Health, Safety & Welfare hours are strongly recommended. Licenses renew biennially by January 1 of odd years."
          }
        },
        {
          "@type": "Question",
          "name": "What are the Montana CPA continuing education requirements?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Montana CPAs must complete 80 CPE hours every 2 years, including 4 hours in professional ethics. At least 20 hours must be completed per calendar year. Biennial renewal is due by December 31 of even years."
          }
        },
        {
          "@type": "Question",
          "name": "How many CLE credits do Montana attorneys need?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Montana attorneys must complete 15 CLE credits per year, including 1 hour in legal ethics or professional responsibility. Up to 6 credits may be completed via online or self-study formats. The annual compliance deadline is December 31."
          }
        }
      ]
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Montana Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Montana professional license CE requirements for architects, engineers, CPAs, attorneys, nurses, and pharmacists. Track all your MT licenses in one place."
        keywords="Montana license renewal, MT CE requirements, Montana continuing education, Montana professional licenses, Montana Board of Public Accountants, Montana State Bar"
        canonical="https://cectrack.com/mt-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                Montana Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Track your continuing education requirements across all your Montana professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to="/register" className="btn btn-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                  Start Tracking Free
                </Link>
                <a href="#professions" className="btn btn-outline-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>6</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>50+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Board Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main id="main-content">
        {/* Professions Grid */}
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
                Montana CE Requirements by Profession
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Select your profession to see detailed continuing education requirements
              </p>
            </div>
            <div className="row g-4">
              {professions.map((prof) => (
                <div key={prof.id} className="col-md-6 col-lg-4">
                  <div
                    className="card h-100 border-0 shadow-sm"
                    style={{ transition: 'all 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'; }}
                  >
                    <div className="card-body p-4">
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                      <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>{prof.name}</h5>
                      <div style={{ backgroundColor: '#e7f3ff', padding: '12px 16px', borderRadius: '8px', marginBottom: '1rem' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                          {prof.credits > 0 ? `${prof.credits} ${prof.terminology}` : 'Mandated Training'}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          {prof.cycleYears === 1 ? 'Annual' : prof.cycleYears === 2 ? 'Biennial' : 'Triennial'} Renewal
                        </div>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Key Requirements:</div>
                        <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                          {prof.highlights.map((h, i) => <li key={i} style={{ marginBottom: '0.3rem' }}>{h}</li>)}
                        </ul>
                      </div>
                      <div style={{ paddingTop: '1rem', borderTop: '1px solid #e9ecef', fontSize: '0.85rem', color: '#6c757d' }}>
                        <strong>{prof.stateBoard}</strong><br />
                        <a href={prof.boardUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>
                          Visit State Board →
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why CEC Track */}
        <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                  Simplify Your Montana License Management
                </h2>
                <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                  CEC Track automatically tracks your requirements, calculates your progress, and sends timely reminders so you never miss a renewal deadline.
                </p>
                <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                  {[
                    'Automated deadline tracking for all Montana professions',
                    'Upload certificates and match to requirements',
                    'Real-time progress reports by category',
                    'Multi-state support for professionals licensed in multiple states',
                    'Secure cloud storage for all documents',
                    'Email notifications for upcoming deadlines'
                  ].map((feature, idx) => (
                    <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-6">
                <div className="p-5 text-center" style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                  <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>Start Tracking Today</h3>
                  <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                    Join professionals across Montana who trust CEC Track to manage their continuing education.
                  </p>
                  <Link to="/register" className="btn btn-primary btn-lg px-5 py-3"
                    style={{ fontWeight: '600', background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', border: 'none', borderRadius: '8px' }}>
                    Create Free Account
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* State Resources */}
        <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
                Montana State Resources
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Official resources and contact information for Montana professional licensing
              </p>
            </div>
            <div className="row g-4">
              <div className="col-md-6">
                <article className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                      Montana Board of Professional Engineers
                    </h3>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                      Regulates professional engineers and land surveyors in Montana under the Department of Labor and Industry.
                    </p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> <a href="tel:+14064448217">(406) 444-8217</a></div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Website:</strong>{' '}
                      <a href="https://boards.bsd.dli.mt.gov/eng" target="_blank" rel="noopener noreferrer">boards.bsd.dli.mt.gov/eng</a>
                    </div>
                    <div style={{ marginBottom: '1rem' }}><strong>Office:</strong> 301 South Park Ave., Helena, MT 59601</div>
                    <a href="https://boards.bsd.dli.mt.gov/eng" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">
                      Visit Montana Engineering Board
                    </a>
                  </div>
                </article>
              </div>
              <div className="col-md-6">
                <article className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                      Montana Board of Public Accountants
                    </h3>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                      Regulates CPA licensure and CPE requirements for Montana certified public accountants.
                    </p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> <a href="tel:+14064448217">(406) 444-8217</a></div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Website:</strong>{' '}
                      <a href="https://boards.bsd.dli.mt.gov/pac" target="_blank" rel="noopener noreferrer">boards.bsd.dli.mt.gov/pac</a>
                    </div>
                    <div style={{ marginBottom: '1rem' }}><strong>Office:</strong> 301 South Park Ave., Helena, MT 59601</div>
                    <a href="https://boards.bsd.dli.mt.gov/pac" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">
                      Visit Montana Board of Accountancy
                    </a>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        <OtherStates currentState="MT" />

        {/* CTA */}
        <section
          aria-label="Call to action"
          style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white', padding: '80px 0' }}
        >
          <div className="container text-center">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
              Ready to Simplify Your Montana License Renewal?
            </h2>
            <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
              Join professionals across Montana who never worry about missing CE requirements
            </p>
            <Link to="/register" className="btn btn-light btn-lg px-5 py-3"
              style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}>
              Get Started Free
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default MTLicenseRenewal;
